import React, { useCallback, useEffect, useState } from 'react';
import {
  getTenantTranslationLanguages,
  setTenantTranslationLanguages,
  SUPPORTED_LANGUAGES,
  languageLabel,
} from '../../../services/translationsAdminService';
import {
  EYEBROW_CLASSNAME,
  TRANSLATION_LANGUAGES_EYEBROW,
} from './tenantLanguageEyebrows';

/**
 * Per-tenant TRANSLATION-LANGUAGE list (feat-260, ADR 0001 §7.3/§7.4).
 *
 * Sits beside `TenantLanguageDefaultCard` on the Tenant dashboard and is
 * deliberately its near-twin in chrome. The two settings were ONE setting until
 * this feature, and that conflation is the bug it exists to fix:
 *
 *   `tenant.default_language` — a READ-side resolution tier: what a trainee sees.
 *   this list                 — a WRITE-side trigger: what gets machine-translated.
 *
 * Until now the only way to make the platform translate curriculum into Spanish
 * was to set the tenant's DEFAULT to Spanish, which also changed what every
 * trainee saw. The pair of eyebrow labels — `What trainees see` here,
 * `What gets translated` there — is what carries the distinction on screen, and
 * is the entire reason this feature adds one `<p>` to the sibling card
 * (spec AT12, `design/copy.md` §1b, `design/argued-lines.md` A2).
 *
 * ## RBAC: THIS CARD IS ALWAYS RENDERED. DO NOT "FIX" IT TO HIDE.
 *
 * When `canManage` is false the controls are `disabled` and a permission note
 * explains why — exactly as `TenantLanguageDefaultCard.tsx:82-86` does, six
 * lines away. RULED 2026-08-25, and the ruling reversed both an earlier draft of
 * the spec and `design/states.md` r1. ADR 0001 §7.4 was CORRECTED at r3 and now
 * says the same thing this comment does, so there is no longer a stale authority
 * to route around. The authorities are `spec.md` AT11, `design/states.md` §8 and
 * ADR 0001 §7.4 — all three agree.
 *
 * Why, so it survives whoever touches this next: `StaffRoute` admits Manager
 * (`App.tsx:265-272`, feat-221's comment), and `TenantDashboard.tsx:1930` grants
 * `canManage` only to Admin/Corporate — so a store manager genuinely reaches
 * this screen with `canManage === false`. That reader is *precisely* the one
 * most likely to conflate "what trainees see" with "what gets translated",
 * having less context than a corporate admin. Hiding one card from them defeats
 * the distinction the pairing exists to create. The conditional-render precedent
 * elsewhere in `TenantDashboard.tsx` ("Withdraw consent", `:2902`) is a
 * DESTRUCTIVE ACTION BUTTON — hiding a verb you cannot use is right for a
 * button and wrong for a settings card, which carries an explanation that has
 * value to a reader who cannot act on it. The general rule, from
 * `design/components-addendum.md`: hide a BUTTON, disable a SETTINGS CARD.
 *
 * ## The load-failed state is the dangerous one (spec AT20)
 *
 * A rejected GET leaves this form all-unchecked — which is IDENTICAL in
 * appearance to a tenant that deliberately translates into nothing, a legitimate
 * configuration (spec AT2/AT4, `states.md` §2). If those two rendered alike with
 * Save enabled, an admin would see a plausible "no languages selected", click
 * Save, and write an EMPTY LIST — turning translation off for that tenant. That
 * is this feature's original bug, re-created through the UI built to prevent it.
 *
 * The defence is two halves and neither works alone: **Save is disabled until a
 * GET has succeeded**, AND an error note outranks the zero-selected note. See
 * `loaded` below.
 *
 * This is not a rare network blip. Backend and frontend deploy on SEPARATE
 * PIPELINES, so this card WILL render against a backend that does not serve the
 * endpoint yet, and every corporate admin who opens the dashboard in that window
 * sees this state. Treat a 404 exactly like a 503 — a `catch` that reads 404 as
 * "no languages configured" is the precise shape of the bug.
 *
 * ## Not an autosave surface
 *
 * feat-259 shipped per-blur autosave on the neighbouring curriculum editor, so
 * an admin arriving from there may assume a checkbox here persists itself. It
 * does not, deliberately: save-on-blur would write a PARTIAL list. `states.md`
 * §4's three-word `Not saved yet.` exists to remove that assumption before it
 * costs anything. Do not wire `useAutoSave` into this card.
 */

const CARD_TESTID = 'tenant-translation-languages-card';

/** `design/copy.md` §1a — final strings, pasted verbatim. Rendered through
 *  constants rather than inline literals so a copy audit can find them: a
 *  literal-text grep returns a true zero for text that is visibly on screen. */
export const TRANSLATION_LANGUAGES_COPY = {
  /** Half of a matched PAIR — see `tenantLanguageEyebrows.ts` for why the two
   *  strings live together and must be reworded together. */
  eyebrow: TRANSLATION_LANGUAGES_EYEBROW,
  heading: 'Translate content into',
  helper:
    'Curriculum saved from now on is machine-translated into the languages checked here. ' +
    'Translations go live immediately, labeled auto-translated, until a reviewer approves them ' +
    'in Translation review.',
  saveIdle: 'Save',
  saveInFlight: 'Saving…',
  loading: 'Loading…',
  dirty: 'Not saved yet.',
  retry: 'Retry',
  zeroSelected:
    'No languages selected — saving curriculum will not produce any translations.',
  permission:
    'Only corporate or admin users can change which languages content is translated into.',
  /**
   * THE SERVER'S ERROR DETAIL IS NOT RENDERED. Spec AT20 hardening, ruled
   * 2026-08-26: the error block shows a FIXED string and never server-authored
   * text. Two reasons, and the second is the one that bites:
   *
   *  - It is a small information-disclosure surface. A 500's `detail` here can
   *    be an aiomysql traceback carrying a UUID; `states.md` §7 says so.
   *  - It is copy the design system does not own and cannot keep stable, put on
   *    the page in a `role="alert"`.
   *
   * These are `design/copy.md` §1a's own strings with the `{error}` slot left
   * EMPTY — not new copy. I have not invented a string, and both read as
   * complete sentences without it: the load-failed line deliberately omits "try
   * again" because the Retry button beside it says that.
   *
   * NOTE FOR DESIGN — `copy.md` §1a still specifies `{error}` in both strings,
   * and its interpolation rules still describe how to render the unbounded
   * server value. That is now superseded by AT20 and needs an amendment; raised
   * in `QUESTIONS-FRONTEND.md` Q4.
   *
   * The detail is not discarded — it goes to the console, which is where a
   * developer diagnosing a deploy-skew 404 will look for it.
   */
  loadFailed: 'Could not load which languages this tenant translates into.',
  saveFailed: 'Could not save — your selection has not been applied.',
  footnote:
    'Unchecking a language does not delete or hide translations that already exist — they keep ' +
    'being served. It only stops new saves from producing that language. Checking a language ' +
    'does not translate content that already exists; ask Bespoke to backfill it.',
} as const;

/**
 * `{languages}` / `{removed}` — human labels, never raw codes. An admin who
 * reads `es` in a confirmation cannot check it against what they clicked.
 * Oxford-less: `Spanish`, `Spanish and Vietnamese`,
 * `Spanish, Vietnamese and Haitian Creole` (`design/copy.md` §1a).
 */
export function joinLanguageLabels(codes: string[]): string {
  const labels = codes.map(languageLabel);
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

function upperFirst(s: string): string {
  return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * The server's message goes to the CONSOLE, never to the DOM (spec AT20
 * hardening). Returns nothing renderable on purpose: a function that returned
 * the string would invite a later `{detail}` back into the JSX, which is the
 * regression this exists to prevent.
 */
function logErrorDetail(label: string, e: unknown): void {
  // eslint-disable-next-line no-console
  console.error(`[TenantTranslationLanguagesCard] ${label}:`, e);
}

/**
 * The checkbox universe BEFORE the GET resolves.
 *
 * `design/states.md` §1 and §2b both require checkboxes to be rendered — and
 * disabled — while loading and after a failed load, so the card never becomes a
 * skeleton or changes height under the reader. But the real universe is the
 * server's `supported_languages` (ADR 0001 §7.4), which has not arrived yet.
 *
 * So: the client-side `SUPPORTED_LANGUAGES` minus `en` stands in as a DISABLED
 * PLACEHOLDER, and is REPLACED — not merged — by the server's list the instant
 * the GET returns. This is NOT the hard-coded list `design/copy.md` §4 forbids,
 * and the difference is not stylistic: Save is disabled in both of the states
 * that use this fallback (`loaded === false`), so no value from it can ever
 * reach a PUT. It decides layout, never data.
 *
 * APPROVED with an addition, `FRONTEND-SLICE.md` r4 §3b (Q3). Two tests hold it,
 * and both had to be written deliberately because the defect they catch is
 * INVISIBLE against today's data — client and server both say `['es']`, so a
 * placeholder that silently became the only source would look identical:
 *
 *   - "THE SWAP: the server universe REPLACES the client placeholder" — gives
 *     the server a universe sharing NO member with the placeholder. Mutating
 *     the replace into a union reddens EXACTLY that one test and nothing else
 *     in the file. Run; see the commit message.
 *   - "the placeholder is DISABLED before the GET resolves" — asserts the
 *     layout-never-data property directly, rather than leaving it as a claim in
 *     this comment.
 *
 * It will still read like a hard-coded `['es']` to a grep. That is why this
 * paragraph is here.
 */
const PLACEHOLDER_UNIVERSE: string[] = SUPPORTED_LANGUAGES.map((l) => l.code).filter(
  (c) => c !== 'en',
);

type Confirmation = { text: string; block: boolean };

/**
 * `design/states.md` §6 — the confirmation restates the consequence of the
 * specific click, which the footnote can only state in the abstract.
 *
 * Diffed against what was LOADED, not against what was SENT: the server
 * normalizes (sorts, dedupes, drops `en`), and `after` is the re-SELECTed truth.
 */
export function buildConfirmation(before: string[], after: string[]): Confirmation {
  const removed = before.filter((code) => !after.includes(code));
  const removedLabels = joinLanguageLabels(removed);

  if (after.length === 0) {
    // The list is now empty. Two sentences — unless nothing was actually
    // removed, in which case the second would name a removal that did not
    // happen. `design/copy.md` keys its three variants on removal and on
    // emptiness and does not enumerate "already empty, saved again"; saving an
    // unchanged empty list is idempotent and legitimate (`states.md` §3), so it
    // needs an answer. Truncating to the true first sentence is the only
    // variant here that does not assert something false.
    return {
      text:
        removed.length === 0
          ? 'Saved. New content saves will not be translated.'
          : `Saved. New content saves will not be translated. ${upperFirst(removedLabels)} ` +
            'translations already in the system are unchanged and are still shown to trainees.',
      block: true,
    };
  }

  if (removed.length > 0) {
    return {
      text:
        `Saved. New content saves will no longer be translated into ${removedLabels}. ` +
        `${upperFirst(removedLabels)} translations already in the system are unchanged and are ` +
        'still shown to trainees.',
      block: true,
    };
  }

  return {
    text: `Saved. New content saves will be translated into ${joinLanguageLabels(after)}.`,
    block: false,
  };
}

const TenantTranslationLanguagesCard: React.FC<{
  tenantId: string;
  canManage: boolean;
}> = ({ tenantId, canManage }) => {
  /** THE AT20 FLAG. False until a GET has SUCCEEDED — not "until a GET has
   *  finished". Every disabled predicate below reads it, so a failed load can
   *  never present an actionable form. */
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [universe, setUniverse] = useState<string[]>(PLACEHOLDER_UNIVERSE);
  const [selected, setSelected] = useState<string[]>([]);
  /** The last set the SERVER confirmed. The diff basis for both `dirty` and the
   *  §6 confirmation, and what a successful save replaces. */
  const [serverSet, setServerSet] = useState<string[]>([]);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getTenantTranslationLanguages(tenantId);
      // `en` is filtered from the universe rather than rendered disabled: a
      // greyed-out English invites "why can't I turn English on?", which has no
      // good answer (`design/copy.md` §4, spec AT6).
      const supported = (data.supported_languages ?? []).filter((c) => c !== 'en');
      const current = (data.translation_languages ?? []).filter((c) => c !== 'en');
      setUniverse(supported);
      setSelected(current);
      setServerSet(current);
      setLoaded(true);
    } catch (e) {
      // NO 404 SPECIAL CASE, deliberately. "This endpoint does not exist yet" is
      // the failure that will actually happen first (deploy skew), and reading
      // it as "no languages configured" is exactly the bug — it converts a
      // deploy window into an empty list the admin can then save for real.
      logErrorDetail('GET translation-languages failed', e);
      setLoadError(TRANSLATION_LANGUAGES_COPY.loadFailed);
      setLoaded(false);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = (code: string) => {
    setSelected((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const handleSave = async () => {
    setSaving(true);
    // Cleared at the START of the save, not the end. A stale `Saved.` sitting
    // under an in-flight request is a lie for the duration (`states.md` §5).
    setSaveError(null);
    setConfirmation(null);
    const before = serverSet;
    try {
      const data = await setTenantTranslationLanguages(tenantId, selected);
      const after = (data.translation_languages ?? []).filter((c) => c !== 'en');
      // Render the SERVER's set, not the checkbox state that was submitted —
      // the server sorts, dedupes and drops `en` (`states.md` §3).
      setSelected(after);
      setServerSet(after);
      setConfirmation(buildConfirmation(before, after));
    } catch (e) {
      // KEEP THE ADMIN'S SELECTION. Reverting to the last server value is one
      // line, feels like correctness, and destroys their work while showing
      // them a message saying so — `states.md` §7 names it the single most
      // likely defect in this slice. `selected` is deliberately untouched here.
      logErrorDetail('PUT translation-languages failed', e);
      setSaveError(TRANSLATION_LANGUAGES_COPY.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  const dirty =
    loaded &&
    (selected.length !== serverSet.length ||
      selected.some((c) => !serverSet.includes(c)));

  // The two AT20 predicates. `!loaded` covers BOTH "still loading" and "load
  // failed", which is the point: the only thing that enables this form is a GET
  // that came back.
  const controlsDisabled = !canManage || !loaded || saving;
  const saveDisabled = !canManage || !loaded || saving;

  /**
   * The inline note slot, which sits in the controls row beside Save.
   * Precedence, and why these cannot collide: a fresh confirmation means
   * `selected === serverSet`, so it is never simultaneously dirty.
   */
  let inlineNote: string | null = null;
  if (loading) inlineNote = TRANSLATION_LANGUAGES_COPY.loading;
  else if (confirmation && !confirmation.block) inlineNote = confirmation.text;
  else if (dirty) inlineNote = TRANSLATION_LANGUAGES_COPY.dirty;

  /**
   * Block-note RANK ORDER. This is the one piece of behaviour neither
   * `states.md` nor the ADR settles, and it is recorded in
   * `QUESTIONS-FRONTEND.md` Q1 with the reasoning.
   *
   * The gap: on this screen `canManage === false` is exactly the Manager role,
   * and spec AT7 gates the READ endpoint on Admin-or-CorporateManager too — so
   * the one population §8 (permission) was written for is the one population
   * whose GET cannot succeed, landing them in §2b (load failed). Both sections
   * describe that user's render; neither anticipates the other.
   *
   * Resolved by ranking rather than choosing: the load error comes FIRST (AT20's
   * "outranks"), the permission note follows it rather than replacing it, and
   * the zero-selected note is suppressed by both. That keeps AT11 and AT20 true
   * at the same time and never states a false thing about the tenant's config.
   *
   * THIS RANKING IS NOT DEAD CODE — and the sentence you are reading is NOT what
   * keeps it. Two tests in `TenantTranslationLanguagesCard.test.tsx` do, and
   * they catch DIFFERENT mutations:
   *
   *   - `shows an error that OUTRANKS the zero-selected note` (AT20 block) —
   *     reddens if the error stops suppressing the zero-selected note.
   *   - `note ranking survives — load-error outranks, zero-selected suppressed`
   *     — reddens if the error REPLACES the permission note instead of ranking
   *     above it, and if the two are emitted in the wrong document order.
   *
   * Neither sees the other's mutation; see that block's header for why. If you
   * are about to delete this as unreachable, run both first — a comment claiming
   * code is reachable is an untested claim, and prose has never stopped anyone
   * deleting code that looks dead. `break-words` is deliberately NOT on the
   * error blocks: `design/states.md` §10 — this card has no unbounded input, and
   * a class pre-accommodating one argues against the invariant.
   *
   * Spec AT7
   * was amended on 2026-08-26 (the fix went to the GATE, not to this render):
   * GET now admits a Manager of that tenant, PUT stays Admin/Corporate. So the
   * collision that motivated the ranking no longer FIRES for a Manager, who now
   * lands in §8 clean with the tenant's real value. The ranking still governs a
   * GENUINE load failure — deploy skew, an expired session, a 500 — in which the
   * error must still outrank everything below it. The solution is correct under
   * both the original and the amended AT7, which is why it survived the
   * amendment unchanged. The concrete surviving case is that test block's
   * `canManage === false` arm: a Manager plus deploy skew, an expired session,
   * or a 500 — error and permission note still co-occur, and this is what
   * orders them.
   *
   * The request is never gated client-side: `DESIGN.md` principle 6 — disabling
   * a control is presentation, the server is the access boundary.
   */
  const showZeroSelected = loaded && canManage && !loadError && selected.length === 0;
  const showOneLanguageNote = loaded && universe.length === 1;

  return (
    <div
      className="mb-6 rounded-lg border border-gray-200 bg-white p-4"
      data-testid={CARD_TESTID}
    >
      <p className={EYEBROW_CLASSNAME}>{TRANSLATION_LANGUAGES_COPY.eyebrow}</p>
      <h2 className="text-lg font-semibold text-ink mb-1">
        {TRANSLATION_LANGUAGES_COPY.heading}
      </h2>
      <p className="text-sm text-inkMuted mb-3">{TRANSLATION_LANGUAGES_COPY.helper}</p>

      <div className="flex items-center gap-3 flex-wrap">
        {universe.map((code) => (
          <label key={code} className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 disabled:opacity-50"
              data-testid={`tenant-translation-language-${code}`}
              checked={selected.includes(code)}
              disabled={controlsDisabled}
              onChange={() => toggle(code)}
              aria-label={languageLabel(code)}
            />
            <span className="text-sm text-ink">{languageLabel(code)}</span>
          </label>
        ))}
        <button
          type="button"
          className="text-sm font-medium px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          disabled={saveDisabled}
          onClick={handleSave}
          data-testid="tenant-translation-languages-save"
        >
          {saving
            ? TRANSLATION_LANGUAGES_COPY.saveInFlight
            : TRANSLATION_LANGUAGES_COPY.saveIdle}
        </button>
        {inlineNote && (
          <span
            className="text-xs text-inkMuted"
            data-testid="tenant-translation-languages-inline-note"
          >
            {inlineNote}
          </span>
        )}
      </div>

      {loadError && (
        <p
          role="alert"
          className="mt-2 text-xs text-danger-text"
          data-testid="tenant-translation-languages-load-error"
        >
          {loadError}
        </p>
      )}
      {loadError && (
        <button
          type="button"
          className="mt-2 text-sm font-medium px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          disabled={loading}
          onClick={() => void load()}
          data-testid="tenant-translation-languages-retry"
        >
          {TRANSLATION_LANGUAGES_COPY.retry}
        </button>
      )}

      {!canManage && (
        <p className="mt-2 text-xs text-inkMuted">
          {TRANSLATION_LANGUAGES_COPY.permission}
        </p>
      )}

      {saveError && (
        <p
          role="alert"
          className="mt-2 text-xs text-danger-text"
          data-testid="tenant-translation-languages-save-error"
        >
          {saveError}
        </p>
      )}

      {confirmation && confirmation.block && (
        <p
          className="mt-2 text-xs text-inkMuted"
          data-testid="tenant-translation-languages-block-confirmation"
        >
          {confirmation.text}
        </p>
      )}

      {showZeroSelected && (
        <p
          className="mt-2 text-xs text-inkMuted"
          data-testid="tenant-translation-languages-zero-selected"
        >
          {TRANSLATION_LANGUAGES_COPY.zeroSelected}
        </p>
      )}

      {showOneLanguageNote && (
        <p className="mt-2 text-xs text-inkMuted">
          {`${languageLabel(universe[0])} is the only language Bespoke translates into today.`}
        </p>
      )}

      <p className="mt-2 text-xs text-inkMuted">{TRANSLATION_LANGUAGES_COPY.footnote}</p>
    </div>
  );
};

export default TenantTranslationLanguagesCard;
